import { useMemo, useEffect, useRef } from "react"
import debounce from "lodash/debounce"

/**
 * Returns a debounced version of the callback function.
 * The debounced function will delay invoking the callback until after wait milliseconds
 * have elapsed since the last time it was invoked.
 *
 * @param callback The function to debounce
 * @param delay The number of milliseconds to delay. Defaults to 300ms.
 * @returns A debounced version of the callback
 */
export const useDebouncedCallback = (
  callback: (...args: any[]) => void,
  delay: number = 300
) => {
  const callbackRef = useRef(callback)

  // Keep callback ref up to date so we always call the latest version
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  // Create debounced version that calls the latest callback from ref
  const debouncedCallback = useMemo(
    () =>
      debounce((...args: any[]) => {
        callbackRef.current(...args)
      }, delay),
    [delay]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedCallback.cancel()
    }
  }, [debouncedCallback])

  return debouncedCallback
}
